import { Eye } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../app/AuthContext";
import type { PersonView } from "../../shared/types/api";
import { cn } from "../../shared/ui";

/**
 * How many people are following this item, and who they are.
 *
 * <p>Worth saying on the item rather than only on each person's own watchlist. Somebody about to
 * change something wants to know who will hear about it, and somebody who has asked a question
 * wants to know whether anybody is listening — both are the same number, and neither is
 * answerable from a list that only shows your own.
 *
 * <p>Shown to everybody who can see the item, not only to whoever may edit it. Who is watching is
 * not an edit.
 */
export function WatchersButton({ watchers }: { watchers: PersonView[] }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const holder = useRef<HTMLDivElement>(null);

  /* Closed by clicking anywhere else, the way every other small panel on the page behaves. */
  useEffect(() => {
    if (!open) {
      return;
    }
    const closeOnOutside = (event: MouseEvent) => {
      if (holder.current && !holder.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [open]);

  if (watchers.length === 0) {
    return null;
  }

  const youAreOne = watchers.some((person) => person.id === user?.employeeId);

  return (
    <div className="relative" ref={holder}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-label={`${watchers.length} watching this item. Show who.`}
        className={cn(
          "inline-flex h-8 items-center gap-1.5 rounded-lg border border-ink-200 px-3 text-sm",
          "text-ink-700 transition-colors hover:bg-ink-50",
          open && "bg-ink-50",
        )}
      >
        <Eye className="h-4 w-4 text-ink-500" />
        {watchers.length} watching
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 rounded-lg border border-ink-200 bg-surface p-1 shadow-lg">
          <p className="px-3 py-2 text-xs text-ink-500">
            These people see everything that happens to this item.
          </p>
          <ul className="max-h-64 overflow-y-auto">
            {watchers.map((person) => (
              <li
                key={person.id}
                className="px-3 py-1.5 text-sm text-ink-800"
              >
                {person.name}
                {person.id === user?.employeeId && (
                  <span className="ml-1.5 text-xs text-ink-500">(you)</span>
                )}
              </li>
            ))}
          </ul>
          {!youAreOne && (
            /* Said rather than offered a button: watching starts by joining the conversation,
               which is the behaviour the whole feature rests on. A second way in would make the
               list of watchers and the list of people who have said something drift apart. */
            <p className="border-t border-ink-100 px-3 py-2 text-xs text-ink-500">
              Comment on it and you will be added.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
